import {
  AllFeedback,
  AllFreeformAnswers,
  AnswerSource,
  AnswerStats,
  Feedback, FreeformAnswer, Question, QuestionStats,
  SurveyAudienceCriteria, SurveyQuestionType, SurveySummaryQuestion,
  SurveyStats,
  TotalUsersStats,
} from "../../redux/survey/survey.types";
import { CombinedQuestionStats } from "./view-survey.types";
import type { SurveyGroupResultsSurveyPayload } from "src/types/survey-group-api.types";

const { AI, All, Human, RTR } = AnswerSource;
const convertCriteriaToList = (criteria: SurveyAudienceCriteria): string[] => {
  return Object.entries(criteria).flatMap(([key, value]) => {
    return (value as string[]).map(val => `${key}: ${val}`);
  });
};

const feedbackListForFilter = (feedback: AllFeedback, sourceFilter: AnswerSource): Feedback[] => {
  const { aiAnswers, humanAnswers, rtrAnswers } = feedback;

  const allFeedback: Feedback[] = [];
  const showHuman = sourceFilter === All || sourceFilter === Human;
  showHuman && allFeedback.push(...humanAnswers);
  const showAI = sourceFilter === All || sourceFilter === AI;
  showAI && allFeedback.push(...aiAnswers);
  const showRTR = sourceFilter === All || sourceFilter === RTR;
  showRTR && allFeedback.push(...rtrAnswers);

  return allFeedback;
};

const freeformListForFilter = (answers: AllFreeformAnswers, sourceFilter: AnswerSource): FreeformAnswer[] => {
  const { aiUsers, humanUsers, rtrUsers } = answers;

  const allAnswers: FreeformAnswer[] = [];
  const showHuman = sourceFilter === All || sourceFilter === Human;
  showHuman && allAnswers.push(...humanUsers);
  const showAI = sourceFilter === All || sourceFilter === AI;
  showAI && allAnswers.push(...aiUsers);
  const showRTR = sourceFilter === All || sourceFilter === RTR;
  showRTR && allAnswers.push(...rtrUsers);

  return allAnswers;
};

/** Stats payloads sometimes use `questions: { [id]: QuestionStats }` (e.g. embedded in survey group results). */
function toQuestionStatsArray(
  questions: QuestionStats[] | Record<string, QuestionStats> | null | undefined
): QuestionStats[] {
  if (questions == null) {
    return [];
  }
  if (Array.isArray(questions)) {
    return questions;
  }
  if (typeof questions === 'object') {
    return Object.values(questions);
  }
  return [];
}

/** Raw stats often use `answers: { [answerId]: AnswerStats }` instead of an array. */
function toAnswerStatsArray(
  answers: AnswerStats[] | Record<string, AnswerStats> | null | undefined
): AnswerStats[] {
  if (answers == null) {
    return [];
  }
  if (Array.isArray(answers)) {
    return answers;
  }
  if (typeof answers === 'object') {
    return Object.values(answers);
  }
  return [];
}

function toSummaryQuestionArray(
  questions: SurveySummaryQuestion[] | Record<string, SurveySummaryQuestion> | null | undefined
): SurveySummaryQuestion[] | undefined {
  if (questions == null) {
    return undefined;
  }
  if (Array.isArray(questions)) {
    return questions;
  }
  if (typeof questions === 'object') {
    return Object.values(questions);
  }
  return undefined;
}

const combineQuestionStats = (
  statsQuestions?: QuestionStats[] | Record<string, QuestionStats>,
  surveyQuestions?: Question[],
  summaryQuestions?: SurveySummaryQuestion[] | Record<string, SurveySummaryQuestion>,
  sourceExtSurveyId?: string
): CombinedQuestionStats[] => {
  if (!surveyQuestions?.length) {
    return [];
  }
  const statsArr = toQuestionStatsArray(statsQuestions);
  if (statsArr.length === 0) {
    return [];
  }
  const summaryArr = toSummaryQuestionArray(summaryQuestions);

  return statsArr.map((question: QuestionStats) => {
    const matchingQuestion = surveyQuestions.find(q => q.id === question.id);
    const hasFeedback = matchingQuestion?.hasFeedback || false;
    const summaries = summaryArr?.find(q => q.id === question.id)?.summaries;

    return {
      ...question,
      answers: toAnswerStatsArray(question.answers as AnswerStats[] | Record<string, AnswerStats>),
      hasFeedback,
      summaries,
      questionType: matchingQuestion?.type as SurveyQuestionType,
      ...(sourceExtSurveyId ? { sourceExtSurveyId } : {}),
    };
  });
};

/** Sum “started” / total users from a stats payload (handles legacy and unified shapes). */
function totalStartedFromStatsPayload(stats: SurveyStats | undefined): number {
  if (!stats) {
    return 0;
  }
  const raw = stats.total_users;
  if (typeof raw === 'number') {
    return raw;
  }
  if (raw && typeof raw === 'object' && 'total' in raw) {
    return typeof raw.total === 'number' ? raw.total : 0;
  }
  return stats.totalUsers ?? 0;
}

/** Sum totals across selected group survey payloads (aggregated user count). */
function sumGroupPayloadUserTotals(payloads: SurveyGroupResultsSurveyPayload[]): number {
  let sum = 0;
  for (const p of payloads) {
    sum += totalStartedFromStatsPayload(p.stats as SurveyStats | undefined);
  }
  return sum;
}

/** Sum unified `total_users` fields across selected survey slices (sidebar / aggregates). */
function aggregateGroupPayloadTotalUsers(payloads: SurveyGroupResultsSurveyPayload[]): TotalUsersStats {
  let total = 0;
  let started = 0;
  let completed = 0;
  for (const p of payloads) {
    const st = p.stats as SurveyStats | undefined;
    const raw = st?.total_users;
    if (typeof raw === 'object' && raw && 'total' in raw) {
      total += raw.total;
      started += raw.started ?? 0;
      completed += raw.completed ?? 0;
    } else if (typeof raw === 'number') {
      total += raw;
    } else {
      total += st?.totalUsers ?? 0;
    }
  }
  return { total, started, completed };
}

export {
  aggregateGroupPayloadTotalUsers,
  combineQuestionStats,
  convertCriteriaToList,
  feedbackListForFilter,
  freeformListForFilter,
  sumGroupPayloadUserTotals,
  totalStartedFromStatsPayload,
};
